<template>
    <div class="space-y-4">
        <!-- DLQ Error Display -->
        <div v-if="dlqError" class="border border-destructive/20 rounded-lg p-4 bg-destructive/5">
            <div class="flex items-start gap-3 mb-3">
                <AlertTriangle class="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <div class="flex-1">
                    <h4 class="font-semibold text-destructive text-base mb-2">Dead Letter Queue Error</h4>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <!-- Timestamp -->
                        <div class="flex flex-col">
                            <span class="text-muted-foreground font-medium">Timestamp:</span>
                            <span class="font-mono text-xs break-all">
                                {{ formatTimestamp(dlqError.timestamp) }}
                            </span>
                        </div>

                        <!-- Offset -->
                        <div class="flex flex-col">
                            <span class="text-muted-foreground font-medium">Message Offset:</span>
                            <Badge variant="outline" class="font-mono text-xs w-fit">
                                {{ dlqError.message_offset }}
                            </Badge>
                        </div>
                    </div>

                    <!-- DLQ Topic Link -->
                    <div class="mt-4 pt-3 border-t border-destructive/10">
                        <span class="text-muted-foreground font-medium text-sm block mb-2">DLQ Topic:</span>
                        <a
                            :href="`/topics/${dlqError.dlq_topic_name}`"
                            target="_blank"
                            class="inline-flex items-center gap-2 text-primary hover:text-primary/80 text-sm font-mono break-all hover:underline bg-muted/30 px-2 py-1 rounded border">
                            <Database class="h-4 w-4 flex-shrink-0" />
                            {{ dlqError.dlq_topic_name }}
                            <ExternalLink class="h-3 w-3 flex-shrink-0" />
                        </a>
                    </div>
                </div>
            </div>

            <!-- Error Message -->
            <div class="mt-4 pt-4 border-t border-destructive/20">
                <div class="flex items-center gap-2 mb-3">
                    <span class="text-muted-foreground font-medium text-sm">Error Message:</span>
                    <div class="flex-1 h-px bg-destructive/10"></div>
                </div>
                <div class="bg-muted/50 rounded-md border max-h-64 overflow-y-auto">
                    <div class="p-3">
                        <component :is="dlqErrorDisplayComponent" :message="processedDlqError" @toggle-section="handleToggleDlqSection" />
                    </div>
                </div>
            </div>
        </div>

        <!-- No DLQ Errors State -->
        <Alert v-else-if="showNoErrorsMessage && !loading" variant="default" class="border-green-200 bg-green-50">
            <CheckCircle class="h-4 w-4 text-green-600" />
            <AlertDescription class="text-green-800">No DLQ errors found.</AlertDescription>
        </Alert>

        <!-- Error State -->
        <div v-if="error" class="mt-3">
            <div class="bg-muted/50 rounded border-l-2 border-destructive max-h-64 overflow-y-auto">
                <div class="p-3">
                    <component :is="errorDisplayComponent" :message="processedError" @toggle-section="handleToggleErrorSection" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, reactive } from 'vue';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Database, ExternalLink, CheckCircle } from 'lucide-vue-next';
import StackTraceDisplay from '@/components/dialogs/error-display/StackTraceDisplay.vue';
import JsonErrorDisplay from '@/components/dialogs/error-display/JsonErrorDisplay.vue';
import SimpleErrorDisplay from '@/components/dialogs/error-display/SimpleErrorDisplay.vue';

const props = defineProps({
    dlqError: {
        type: Object,
        default: null
    },
    loading: {
        type: Boolean,
        default: false
    },
    error: {
        type: [Object, String, Error],
        default: null
    },
    showNoErrorsMessage: {
        type: Boolean,
        default: false
    }
});

// Reactive state for collapsible sections
const dlqErrorCollapsedSections = reactive(new Set());
const errorCollapsedSections = reactive(new Set());

// Handlers for toggling collapsible sections
const handleToggleDlqSection = (sectionId) => {
    if (dlqErrorCollapsedSections.has(sectionId)) {
        dlqErrorCollapsedSections.delete(sectionId);
    } else {
        dlqErrorCollapsedSections.add(sectionId);
    }
};

const handleToggleErrorSection = (sectionId) => {
    if (errorCollapsedSections.has(sectionId)) {
        errorCollapsedSections.delete(sectionId);
    } else {
        errorCollapsedSections.add(sectionId);
    }
};

/**
 * Format timestamp for display
 */
const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';

    try {
        const date = new Date(timestamp);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZoneName: 'short'
        });
    } catch (error) {
        console.error('Error formatting timestamp:', error);
        return timestamp;
    }
};

// DLQ Error message processing
const dlqErrorString = computed(() => {
    if (!props.dlqError?.error_message) return '';
    return props.dlqError.error_message;
});

const detectedDlqErrorType = computed(() => {
    const error = dlqErrorString.value;

    if (isComplexError(error)) return 'Complex';
    if (isStackTrace(error)) return 'Stack Trace';
    if (isJsonError(error)) return 'JSON';
    if (isValidationError(error)) return 'Validation';
    if (isSqlError(error)) return 'SQL';

    return 'Text';
});

const processedDlqError = computed(() => {
    const error = dlqErrorString.value;
    const type = detectedDlqErrorType.value;

    // Always split by newlines for plain text processing
    // SimpleErrorDisplay will handle the formatting and apply color enhancements
    const lines = error.split('\n');

    return {
        raw: error,
        type,
        lines: lines,
        searchTerm: '',
        collapsedSections: dlqErrorCollapsedSections
    };
});

const dlqErrorDisplayComponent = computed(() => {
    switch (detectedDlqErrorType.value) {
        case 'Stack Trace':
            return StackTraceDisplay;
        case 'Complex':
        case 'JSON':
        case 'Validation':
            return JsonErrorDisplay;
        default:
            return SimpleErrorDisplay;
    }
});

// General error display (for API errors, not DLQ errors)
// Error type detection for dynamic component selection
const errorString = computed(() => {
    if (!props.error) return '';
    return typeof props.error === 'string' ? props.error : String(props.error);
});

const detectedErrorType = computed(() => {
    const error = errorString.value;

    if (isComplexError(error)) return 'Complex';
    if (isStackTrace(error)) return 'Stack Trace';
    if (isJsonError(error)) return 'JSON';
    if (isValidationError(error)) return 'Validation';
    if (isSqlError(error)) return 'SQL';

    return 'Text';
});

const processedError = computed(() => {
    const error = errorString.value;
    const type = detectedErrorType.value;

    // Always split by newlines for plain text processing
    // SimpleErrorDisplay will handle the formatting and apply color enhancements
    const lines = error.split('\n');

    return {
        raw: error,
        type,
        lines: lines,
        searchTerm: '',
        collapsedSections: errorCollapsedSections
    };
});

const errorDisplayComponent = computed(() => {
    switch (detectedErrorType.value) {
        case 'Stack Trace':
            return StackTraceDisplay;
        case 'Complex':
        case 'JSON':
        case 'Validation':
            return JsonErrorDisplay;
        default:
            return SimpleErrorDisplay;
    }
});

// Error type detection functions (copied from ErrorDialog.vue)
function isComplexError(error) {
    // Prevent ReDoS by limiting input length
    if (!error || typeof error !== 'string' || error.length > 10000) {
        return false;
    }

    const complexPatterns = [
        /^[^:]{1,100}:\s*\[[^\[\]]{0,500}"[^"]{0,500}\\n[^"]{0,500}"[^\[\]]{0,500}\]/s,
        /\[[^\[\]]{0,300}"[^"]{0,300}(?:at\s+|Exception|Error:|\.java:|\.scala:|\.py:|\.js:|\.ts:|\.go:|\.rs:|\.cpp:|\.c\b)[^"]{0,300}\\n[^"]{0,300}"[^\[\]]{0,300}\]/s,
        /(?:failed|error|exception|crashed|timeout|denied)[^\[]{0,100}\[[^\[\]]{0,300}"[^"]{0,300}(?:ERROR|WARN|INFO|DEBUG|FATAL)[^"]{0,300}\\n[^"]{0,300}"[^\[\]]{0,300}\]/is,
        /\[.*".*\d{2}:\d{2}:\d{2}.*(?:ERROR|Exception).*\\n.*".*\]/s,
        /^.+?:\s*\[.*".*(?:Caused by|at\s+\w+\.|Exception in thread).*\\n.*".*\]/s,
        /\[.*".*\\n.*(?:at\s+[\w.]+\(|org\.|com\.|java\.|scala\.|Exception:|Error:).*".*\]/s,
        /(?:deploy|build|compilation|execution).*failed.*\[.*".*\\n.*".*\]/is,
        // Simplified pattern to avoid ReDoS - check for multiple error entries
        /\[[^\[\]]{0,1000}(?:error|exception|fail|at\s+|caused by)[^\[\]]{0,1000}\]/is,
        /^[^[]*\[.*".*\\n.*".*\].*[\n\r].*(?:at\s+|Exception|Error:|Caused by)/s,
        /\[[^\[\]]{0,300}"[^"]{0,300}\\[nt][^"]{0,300}(?:ERROR|WARN|Exception|at\s+|Caused by|\.java:|\.scala:|\.py:)[^"]{0,300}\\[nt][^"]{0,300}"[^\[\]]{0,300}\]/s
    ];

    const structuralComplexity =
        error.includes('[') &&
        error.includes('"') &&
        error.includes('\\n') &&
        /at\s+[\w.]{1,100}\(|Exception:|Error:|Caused by:/i.test(error.substring(0, 2000)) &&
        (error.match(/"[^"]{0,500}(?:ERROR|WARN|Exception|at\s+)[^"]{0,500}"/g) || []).length >= 2;

    return complexPatterns.some((pattern) => {
        try {
            return pattern.test(error);
        } catch (e) {
            // If regex fails, fall back to simple string matching
            console.warn('Regex error in isComplexError:', e);
            return false;
        }
    }) || structuralComplexity;
}

function isStackTrace(error) {
    if (!error || typeof error !== 'string' || error.length > 10000) {
        return false;
    }

    const stackTracePatterns = [
        /at .+\(.+:\d+:\d+\)/,
        /\s+at .+\(.+\.\w+:\d+\)/,
        /\w+Error: .+\n\s+at/,
        /File ".+", line \d+/,
        /\tat .+\.\w+\(.+\.\w+:\d+\)/,
        /\n\s{2,}at\s/
    ];
    return stackTracePatterns.some((pattern) => pattern.test(error));
}

function isJsonError(error) {
    if (!error || typeof error !== 'string' || error.length > 10000) {
        return false;
    }

    try {
        JSON.parse(error);
        return true;
    } catch {
        return error.includes('{') && error.includes('}') && (error.includes('"') || error.includes("'"));
    }
}

function isValidationError(error) {
    if (!error || typeof error !== 'string' || error.length > 10000) {
        return false;
    }

    const validationPatterns = [
        /validation error/i,
        /field .+ is required/i,
        /invalid .+ format/i,
        /must be .+ characters/i,
        /does not match pattern/i
    ];
    return validationPatterns.some((pattern) => pattern.test(error));
}

function isSqlError(error) {
    if (!error || typeof error !== 'string' || error.length > 10000) {
        return false;
    }

    const sqlPatterns = [/sql/i, /database/i, /query/i, /table .+ doesn't exist/i, /column .+ not found/i, /syntax error/i];
    return sqlPatterns.some((pattern) => pattern.test(error));
}
</script>

<style scoped>
/* Ensure proper text wrapping for long topic names and error messages */
.break-words {
    word-wrap: break-word;
    overflow-wrap: break-word;
}
</style>
